import React, { useEffect, useState } from 'react'
import { Role, TrackingRows } from '@types'
import './track.css'
import {
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TextField,
  Switch,
  TableSortLabel,
  TablePagination,
  Button,
  Select,
  MenuItem,
  Checkbox
} from '@material-ui/core'
import {
  getExecutives,
  getPersonalTracking,
  selectPersonalTracking,
  selectTeamTracking,
  selectTrackingLoader
} from './state/trackingSlice2'
import { useDispatch, useSelector } from 'react-redux'
import TrackingBody from './TableBody'
import { userProfile } from '@views/profile/state/profileSlice'
import { formatPesos } from '../../utils/format'

const defaultOfficeRoles = [Role.casos, Role.operaciones]

export type Order = 'asc' | 'desc'

const defaultTeamRole = [Role.admin, Role.operaciones, Role.casos]

const TrackingView: React.FC = () => {
  const { role } = useSelector(userProfile)
  const [isTeam, setIsTeam] = useState(defaultTeamRole.includes(role))
  const teamRows: TrackingRows[] = useSelector(selectTeamTracking)
  const isLoading = useSelector(selectTrackingLoader)
  const personalRows: TrackingRows[] = useSelector(selectPersonalTracking)
  const rows = isTeam ? teamRows : personalRows
  const [order, setOrder] = useState<Order>('desc')
  const [isNumeric, setIsNumeric] = useState(false)
  const [isRejected, setIsRejected] = useState(false)
  const [isReturned, setIsReturned] = useState(false)
  const [isCrmOnly, setIsCrmOnly] = useState(false)
  const [isDateSort, setIsDateSort] = useState(true)
  const [orderBy, setOrderBy] = useState<string>(defaultOfficeRoles.includes(role) ? 'office' : 'createdAt')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const dispatch = useDispatch()
  const [executiveSearch, setExecutiveSearch] = useState('')
  const [bankSearch, setBankSearch] = useState('')
  const [searchKey, setSearchKey] = useState('')
  const [searchRef, setSearchRef] = useState('')
  const [searchStage, setSearchStage] = useState<'studyDate' | 'office' | 'bank' | 'success' | 'rejected' | 'returned'>(
    'studyDate'
  )
  const [searchStageKey, setSearchStageKey] = useState('')

  const createSortHandler = (property: string, isNumeric: boolean) => () => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    setIsNumeric(isNumeric)
    setIsDateSort(property === 'studyDate')
  }

  useEffect(() => {
    dispatch(getExecutives())
    dispatch(getPersonalTracking())
    setPage(0)
    setRowsPerPage(10)
  }, [])

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const onChangeStageSearch = (value: string) => {
    setSearchStageKey(value)
  }

  const onChangeUser = (value: string) => {
    setSearchKey(value)
  }

  const onChangeRef = (value: string) => {
    setSearchRef(value)
  }

  const filteredRows = rows.filter(
    user =>
      user.bankName
        ?.toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .includes(
          bankSearch
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
        ) &&
      user.executive?.name
        ?.toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .includes(
          executiveSearch
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
        ) &&
      user.parentName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .includes(
          searchRef
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
        ) &&
      user.name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .includes(
          searchKey
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
        ) &&
      user[searchStage]
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .includes(
          searchStageKey
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
        ) &&
      user.isRejected === isRejected &&
      user.isReturned === isReturned &&
      (!isCrmOnly || user.isCrm === true)
  )

  const totalFee = formatPesos(filteredRows.reduce((a, b) => a + (b.isRejected ? 0 : Number(b.fee)), 0))
  const totalPayments = formatPesos(filteredRows.reduce((a, b) => a + Number(b.payments), 0))

  const handleClear = () => {
    setSearchStageKey('')
    setSearchKey('')
    setSearchRef('')
    setSearchStage('studyDate')
    setIsCrmOnly(false)
    setOrderBy('createdAt')
    setOrder('desc')
    setPage(0)
  }
  return (
    <>
      <div className="center_view padding_view">
        <TableContainer className="container-table" component={Paper}>
          <div className="table-container-search">
            <TextField
              id="outlined-multiline-flexible"
              label="Ahorrador"
              value={searchKey}
              onChange={event => onChangeUser(event.target.value)}
              variant="outlined"
              className="input-search"
            />
            <TextField
              id="outlined-multiline-flexible"
              label="Referenciador"
              value={searchRef}
              onChange={event => onChangeRef(event.target.value)}
              variant="outlined"
              className="input-search"
            />
            {role === Role.admin && (
              <TextField
                id="outlined-multiline-flexible-executive"
                label="Ejecutivo"
                value={executiveSearch}
                onChange={event => setExecutiveSearch(event.target.value)}
                variant="outlined"
                className="input-search"
              />
            )}
            {role === Role.operaciones && (
              <TextField
                id="outlined-multiline-flexible-executive"
                label="Banco"
                value={bankSearch}
                onChange={event => setBankSearch(event.target.value)}
                variant="outlined"
                className="input-search"
              />
            )}
            <Box>
              <Select
                variant="outlined"
                required
                value={searchStage}
                onChange={e =>
                  setSearchStage(
                    e.target.value as 'studyDate' | 'office' | 'bank' | 'success' | 'rejected' | 'returned'
                  )
                }
              >
                <MenuItem value="office">Oficina</MenuItem>
                <MenuItem value="studyDate">Estudio</MenuItem>
                <MenuItem value="bank">Radicado</MenuItem>
                <MenuItem value="success">Exitoso</MenuItem>
                <MenuItem value="rejected">Rechazado</MenuItem>
                <MenuItem value="returned">Devuelto</MenuItem>
              </Select>
              <TextField
                id="outlined-multiline-flexible"
                label="Fecha"
                placeholder="AAAA/MM/DD"
                value={searchStageKey}
                onChange={event => onChangeStageSearch(event.target.value)}
                variant="outlined"
                className="input-search"
              />
            </Box>

            {role !== Role.referenciador && (
              <>
                <Box>
                  Rechazados
                  <Checkbox
                    name="Negado"
                    checked={isRejected}
                    onChange={e => {
                      setOrderBy(e.target.checked ? 'rejected' : 'createdAt')
                      setOrder('desc')
                      setIsRejected(e.target.checked)
                    }}
                  />
                </Box>
                <Box>
                  Devueltos
                  <Checkbox name="Devuelto" checked={isReturned} onChange={e => setIsReturned(e.target.checked)} />
                </Box>
              </>
            )}
            {[Role.admin, Role.ejecutivo, Role.gerente].includes(role) && (
              <Box>
                CRM
                <Checkbox
                  name="CRM"
                  checked={isCrmOnly}
                  onChange={event => {
                    setIsCrmOnly(event.target.checked)
                    setPage(0)
                  }}
                />
              </Box>
            )}
            {role !== Role.operaciones ? (
              <Box className="box-head">
                <Box style={{ color: isTeam ? 'lightgray' : '#ff6900' }}>Personal</Box>
                <Switch
                  checked={isTeam}
                  onChange={e => {
                    setIsTeam(e.target.checked)
                  }}
                />

                <Box style={{ color: isTeam ? '#ff6900' : 'lightgray' }}>Equipo</Box>
              </Box>
            ) : (
              ''
            )}
            <Button type="submit" variant="contained" color="primary" onClick={handleClear}>
              Limpiar
            </Button>
          </div>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell
                  className="table_header"
                  style={{ color: 'white' }}
                  align="left"
                  sortDirection={orderBy === 'progress' ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={createSortHandler('name', false)}
                  >
                    AHORRADOR
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  className="table_header"
                  style={{ color: 'white' }}
                  align="left"
                  sortDirection={orderBy === 'bankName' ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === 'bankName'}
                    direction={orderBy === 'bankName' ? order : 'asc'}
                    onClick={createSortHandler('bankName', false)}
                    style={{ color: 'white' }}
                  >
                    BANCO
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  className="table_header"
                  style={{ color: 'white' }}
                  align="center"
                  sortDirection={orderBy === 'studyDate' ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === 'studyDate'}
                    direction={orderBy === 'studyDate' ? order : 'asc'}
                    onClick={createSortHandler('studyDate', false)}
                  >
                    ESTUDIO
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  className="table_header"
                  style={{ color: 'white' }}
                  align="center"
                  sortDirection={orderBy === 'office' ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === 'office'}
                    direction={orderBy === 'office' ? order : 'asc'}
                    onClick={createSortHandler('office', false)}
                  >
                    OFICINA
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  className="table_header"
                  style={{ color: 'white' }}
                  align="center"
                  sortDirection={orderBy === 'bank' ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === 'bank'}
                    direction={orderBy === 'bank' ? order : 'asc'}
                    onClick={createSortHandler('bank', false)}
                  >
                    RADICADO
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  className="table_header"
                  style={{ color: 'white' }}
                  align="center"
                  sortDirection={orderBy === 'success' ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === 'success'}
                    direction={orderBy === 'success' ? order : 'asc'}
                    onClick={createSortHandler('success', false)}
                  >
                    EXITOSO
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  className="table_header"
                  style={{ color: 'white' }}
                  align="center"
                  sortDirection={orderBy === 'fee' ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === 'fee'}
                    direction={orderBy === 'fee' ? order : 'asc'}
                    onClick={createSortHandler('fee', true)}
                  >
                    HONORARIOS
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  className="table_header"
                  style={{ color: 'white' }}
                  align="center"
                  sortDirection={orderBy === 'payments' ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === 'payments'}
                    direction={orderBy === 'payments' ? order : 'asc'}
                    onClick={createSortHandler('payments', true)}
                  >
                    ABONOS
                  </TableSortLabel>
                </TableCell>

                <TableCell className="table_header" style={{ color: 'white' }} align="center"></TableCell>
              </TableRow>
            </TableHead>
            <TrackingBody
              users={filteredRows}
              rowsPerPage={rowsPerPage}
              order={order}
              page={page}
              isNumeric={isNumeric}
              isDateSort={isDateSort}
              orderBy={orderBy}
              isLoading={!!isLoading}
              isTeam={isTeam}
            />
          </Table>
          <div className="pagination-row">
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
            <div>Total Honorarios: ${totalFee}</div>
            <div>Total Abonos: ${totalPayments}</div>
          </div>
        </TableContainer>
      </div>
    </>
  )
}

export default TrackingView
